import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';

import { FormHeaderWithStatus } from '@/components/layout/form-header-with-status';
import { Card } from '@/components/ui/card';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { FormActionButtons } from '@/components/layout/form-action-buttons';
import { useEntityForm } from '@/hooks/useEntityForm';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useTicketGenerator } from '@/hooks/useTicketGenerator';
import { VehicleAutocomplete } from '@/components/ui/vehicle-autocomplete';
import type { VehicleViewModel } from '@/graphql/generated';

import type { ParkingSessionFormSubmitData } from './parking-session-input';

const createParkingSessionFormSchema = (t: (key: string) => string) =>
	z.object({
		plateNumber: z
			.string()
			.min(1, t('parkingSessions:validation.plateNumber.required'))
			.max(50, t('parkingSessions:validation.plateNumber.maxLength'))
			.transform(val => val.trim()),
		ticketId: z
			.string()
			.max(50, t('parkingSessions:validation.ticketId.maxLength'))
			.optional()
			.nullable()
			.or(z.literal(''))
	});

export type ParkingSessionFormValues = z.infer<
	ReturnType<typeof createParkingSessionFormSchema>
>;

interface ParkingSessionFormProps {
	mode: 'create' | 'edit';
	initialData?: ParkingSessionFormSubmitData;
	onSubmit: (data: ParkingSessionFormSubmitData) => void;
	isSubmitting: boolean;
	headerTitle: string;
	onBack?: () => void;
}

export const ParkingSessionForm = ({
	mode,
	initialData,
	onSubmit,
	isSubmitting,
	headerTitle,
	onBack
}: ParkingSessionFormProps) => {
	const { t } = useTranslation();
	const parkingSessionFormSchema = useMemo(
		() => createParkingSessionFormSchema(t),
		[t]
	);

	const form = useEntityForm<ParkingSessionFormValues>({
		mode,
		schema: parkingSessionFormSchema,
		defaultValues: {
			plateNumber: '',
			ticketId: ''
		},
		initialData: initialData as ParkingSessionFormValues | undefined
	});

	useUnsavedChanges(form.formState.isDirty, {
		isSubmitSuccessful: form.formState.isSubmitSuccessful,
		isSubmitting
	});

const { control, handleSubmit, setValue, watch } = form;

	// Generate ticket ID automatically for create mode
	const { generateDynamic, isReady } = useTicketGenerator(0); // siteId 0

	useEffect(() => {
		if (mode === 'create' && isReady) {
			generateDynamic().then(ticketId => {
				setValue('ticketId', ticketId);
			});
		}
	}, [mode, isReady, generateDynamic, setValue]);

	// Track selected vehicle for display in autocomplete
	const plateNumber = watch('plateNumber');
	const [selectedVehicleId, setSelectedVehicleId] = useState<string>(
		initialData?.vehicleId ?? ''
	);

	const handleVehicleChange = (
		vehicleId: string,
		vehicle: VehicleViewModel | null
	) => {
		setSelectedVehicleId(vehicleId);
		if (vehicle?.plateNumber) {
			setValue('plateNumber', vehicle.plateNumber, { shouldValidate: true, shouldDirty: true });
		} else {
			setValue('plateNumber', '', { shouldValidate: true, shouldDirty: true });
		}
	};

	const handleFormSubmit = (values: ParkingSessionFormValues) => {
		onSubmit({
			...values,
			parkingSessionId: initialData?.parkingSessionId
		});
	};

	return (
		<>
			<FormHeaderWithStatus
				title={headerTitle}
				onBack={onBack}
				control={control}
				rightContent={
					<FormActionButtons
						mode={mode}
						isSubmitting={isSubmitting}
						validationErrors={form.formState.errors}
						formId="parking-session-form"
					/>
				}
			/>

			<Form {...form}>
				<form
					id="parking-session-form"
					onSubmit={handleSubmit(handleFormSubmit)}
					className="space-y-6"
				>
					<Card className="p-6">
						<div className="grid gap-6 md:grid-cols-2">
							{/* Vehicle / Plate Number Field */}
							<FormField
								control={control}
								name="plateNumber"
								render={({ fieldState }) => (
									<FormItem>
										<FormLabel>
											{t('parkingSessions:form.vehicle')}
										</FormLabel>
										<VehicleAutocomplete
											value={selectedVehicleId}
											onChange={handleVehicleChange}
											placeholder={t(
												'parkingSessions:form.vehiclePlaceholder'
											)}
											enableCreateNew={true}
											className="w-full"
											openDelay={500}
											initialSelectedLabel={plateNumber || undefined}
											autoFocus={mode === 'create'}
										/>
										{fieldState.error && (
											<FormMessage />
										)}
									</FormItem>
								)}
							/>

							{/* Ticket ID Field (only in create mode) */}
							{mode === 'create' && (
								<FormField
									control={control}
									name="ticketId"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												{t('parkingSessions:form.ticketId')}
											</FormLabel>
											<FormControl>
												<Input
													{...field}
													value={field.value ?? ''}
													readOnly
													className="bg-muted"
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							)}
						</div>
					</Card>
				</form>
			</Form>
		</>
	);
};
